import React from 'react';
import { Box, Typography, Chip, Breadcrumbs, Link } from '@mui/material';
import { useKBTheme } from '../hooks/useKBTheme';
import { useNavigate } from 'react-router-dom';
import { InteractiveDemo } from './InteractiveDemo';

interface ArticleViewProps {
  article: {
    id: string;
    title: string;
    category: string;
    tags?: string[];
    content: React.ReactNode;
    demo?: React.ReactNode;
    lastUpdated?: string;
  };
}

export const ArticleView: React.FC<ArticleViewProps> = ({ article }) => {
  const { mode } = useKBTheme();
  const navigate = useNavigate();

  const getBackgroundGradient = () => {
    if (mode === 'dark') {
      return 'linear-gradient(180deg, #0f172a 0%, #151b26 25%, #1a2332 50%, #1c2838 75%, #1e293b 100%)';
    }
    return 'linear-gradient(180deg, #FAFBFC 0%, #FAFAFC 25%, #F9FAFB 50%, #F9FAFC 75%, #F8FAFC 100%)';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: getBackgroundGradient(),
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: mode === 'dark'
            ? `radial-gradient(circle at 20% 80%, rgba(99,102,241,0.08) 0%, transparent 50%),
               radial-gradient(circle at 80% 20%, rgba(236,72,153,0.08) 0%, transparent 50%)`
            : `radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
               radial-gradient(circle at 80% 20%, rgba(236, 72, 153, 0.05) 0%, transparent 50%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, pt: 4, pb: 8 }}>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3 }}>
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/knowledge-base')}
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Knowledge Base
            </Link>
            <Typography
              variant="body2"
              sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b' }}
            >
              {article.category}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#94a3b8' }}
            >
              {article.title}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', lg: 'row' } }}>
            <Box sx={{ flex: { xs: 1, lg: '0 0 60%' } }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  color: mode === 'dark' ? '#fff' : '#0f172a',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  lineHeight: 1.2,
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                }}
              >
                {article.title}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 4, flexWrap: 'wrap' }}>
                <Chip
                  label={article.category}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
                {article.tags?.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{
                      background: mode === 'dark'
                        ? 'rgba(71, 85, 105, 0.4)'
                        : 'rgba(241, 245, 249, 0.8)',
                      color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b',
                    }}
                  />
                ))}
              </Box>

              <Box
                sx={{
                  color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                  '& h2': {
                    fontSize: '1.75rem',
                    fontWeight: 700,
                    mt: 4,
                    mb: 2,
                    color: mode === 'dark' ? '#fff' : '#0f172a',
                  },
                  '& h3': {
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mt: 3,
                    mb: 1.5,
                    color: mode === 'dark' ? '#fff' : '#0f172a',
                  },
                  '& p': {
                    mb: 2,
                  },
                  '& ul, & ol': {
                    pl: 3,
                    mb: 2,
                  },
                  '& li': {
                    mb: 1,
                  },
                }}
              >
                {article.content}
              </Box>

              {article.lastUpdated && (
                <Typography
                  variant="caption"
                  sx={{
                    color: mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#94a3b8',
                    fontSize: '0.75rem',
                    mt: 4,
                    display: 'block',
                  }}
                >
                  Last updated: {article.lastUpdated}
                </Typography>
              )}
            </Box>

            {article.demo && (
              <Box sx={{ flex: { xs: 1, lg: '0 0 40%' }, position: { lg: 'sticky' }, top: 100, height: { lg: 'calc(100vh - 120px)' } }}>
                <InteractiveDemo>{article.demo}</InteractiveDemo>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};








